import * as React from 'react';
import TempRequestsList from './TempRequestsList';
import TempRequestsFilterBar, { FilterState } from './TempRequestsFilterBar';
import { TempRequest } from '../types';
import moment from 'moment';

interface ManageTempRequestsProps {
  tempRequests: TempRequest[];
  tempJobs: TempRequest[];
  rejectedJobs: TempRequest[];
  authenticity_token: string;
  editProfilePath: string;
}

// Helper to get status for a temp request
const getTempRequestStatus = (request: TempRequest): string => {
  // Check cancelled first (highest priority)
  if (request.cancelled) return 'cancelled';

  if (request.accepted_by_professional === false) return 'declined';
  if (request.accepted_by_professional && request.accepted_by_practice) return 'accepted';
  if (request.accepted_by_professional === null) return 'pending';
  // Expired/closed requests (not responded and job date passed or job closed)
  const jobDatePassed = moment(request.job.temp_date).isBefore(moment(), 'day');
  if (request.accepted_by_professional === null && jobDatePassed) return 'expired';
  return 'pending';
};

const ManageTempRequests: React.FC<ManageTempRequestsProps> = ({
  tempRequests,
  tempJobs,
  rejectedJobs,
  authenticity_token,
  editProfilePath
}) => {
  // Combine all temp requests into one array
  const allTempRequests = React.useMemo(() => {
    return [...tempRequests, ...tempJobs, ...rejectedJobs];
  }, [tempRequests, tempJobs, rejectedJobs]);

  // Filter state
  const [filters, setFilters] = React.useState<FilterState>({
    statuses: new Set(),
    practiceSearch: "",
    positionType: "",
    dateFrom: "",
    dateTo: ""
  });

  // Apply filters to temp requests
  const filteredTempRequests = React.useMemo(() => {
    return allTempRequests.filter((request) => {
      // Status filter
      if (filters.statuses.size > 0) {
        const status = getTempRequestStatus(request);
        if (!filters.statuses.has(status)) {
          return false;
        }
      }

      // Practice search filter
      if (filters.practiceSearch) {
        const practiceName = request.job.practice.name.toLowerCase();
        const searchTerm = filters.practiceSearch.toLowerCase();
        if (!practiceName.includes(searchTerm)) {
          return false;
        }
      }

      // Position type filter
      if (filters.positionType) {
        if (request.job.job_type.title !== filters.positionType) {
          return false;
        }
      }

      // Date range filter
      if (filters.dateFrom || filters.dateTo) {
        const jobDate = moment(request.job.temp_date);
        if (filters.dateFrom && jobDate.isBefore(moment(filters.dateFrom), 'day')) {
          return false;
        }
        if (filters.dateTo && jobDate.isAfter(moment(filters.dateTo), 'day')) {
          return false;
        }
      }

      return true;
    });
  }, [allTempRequests, filters]);

  const getTotalCount = () => allTempRequests.length;

  return (
    <div className="container">
      <div className="row browsing-font mt-5 px-2">
        <div className="col-12 card shadow mb-4 px-0 browsing-card-design" id="temp-requests-list">
          <div className="card-header browsing-cards-head">
            <i className="fas fa-clock"></i> My Temp Requests
            {getTotalCount() > 0 && (
              <span className="badge bg-primary ms-2">{getTotalCount()}</span>
            )}
          </div>

          {getTotalCount() === 0 ? (
            <div className="padding-right p-2">
              <div
                style={{
                  background: 'white',
                  border: '1px solid #e8ecef',
                  borderRadius: '0.75rem',
                  padding: '3rem 1.5rem',
                  textAlign: 'center',
                  margin: '1rem',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
                }}
              >
                <img
                  src="/images/missing.svg"
                  alt=""
                  height={100}
                  style={{ marginBottom: '1rem' }}
                />
                <h4 style={{ color: '#333', marginBottom: '0.5rem', fontWeight: 600 }}>
                  No Temp Requests Yet
                </h4>
                <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
                  Once employers request you for temporary positions, they will appear here.
                </p>
                <a className="btn btn-primary btn-lg" href={editProfilePath}>
                  <i className="fas fa-user-edit me-2"></i>
                  Edit Profile
                </a>
              </div>
            </div>
          ) : (
            <div className="p-3">
              {/* Filter Bar */}
              <TempRequestsFilterBar
                filters={filters}
                onFiltersChange={setFilters}
                tempRequests={allTempRequests}
              />

              {/* Results Count */}
              {filters.statuses.size > 0 || filters.practiceSearch || filters.positionType || filters.dateFrom || filters.dateTo ? (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.75rem 1rem',
                    background: '#f8f9fa',
                    border: '1px solid #e8ecef',
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    color: '#6c757d'
                  }}
                >
                  <i className="fa fa-info-circle me-2"></i>
                  Showing <strong>{filteredTempRequests.length}</strong> of <strong>{getTotalCount()}</strong> temp request{getTotalCount() !== 1 ? 's' : ''}
                </div>
              ) : null}

              {/* Temp Requests List */}
              <TempRequestsList
                tempRequests={filteredTempRequests}
                type="all"
                authenticity_token={authenticity_token}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageTempRequests;
